import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SubscriptionPanel = ({ subscription, onUpgrade }) => {
  // Show empty state if no subscription data
  if (!subscription) {
    return (
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4 md:p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg md:text-xl font-semibold text-[#111827]">Subscription</h3>
        </div>
        <div className="text-center py-8">
          <Icon name="CreditCard" size={48} color="#9CA3AF" className="mx-auto mb-4" />
          <p className="text-[#6B7280] mb-4">No active subscription</p>
          <Button
            variant="default"
            iconName="Zap"
            iconPosition="left"
            onClick={onUpgrade}
          >
            Get Started
          </Button>
        </div>
      </div>
    );
  }

  const usagePercentage = (subscription?.revealsUsed / subscription?.revealsTotal) * 100;

  const getUsageColor = () => {
    if (usagePercentage >= 80) return 'bg-[#EF4444]';
    if (usagePercentage >= 60) return 'bg-[#F59E0B]';
    return 'bg-[#10B981]';
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg md:text-xl font-semibold text-[#111827]">Subscription</h3>
        <span className="px-3 py-1 bg-[#5B2DFF]/10 text-[#5B2DFF] text-sm font-medium rounded-full">
          {subscription?.plan}
        </span>
      </div>
      <div className="space-y-6">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-[#6B7280]">Reveal Credits</span>
            <span className="text-sm font-medium text-[#111827]">
              {subscription?.revealsUsed || 0} / {subscription?.revealsTotal || 0}
            </span>
          </div>
          <div className="w-full h-2 bg-[#F6F7FB] rounded-full overflow-hidden">
            <div
              className={`h-full ${getUsageColor()} transition-all duration-250`}
              style={{ width: `${usagePercentage || 0}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-[#F6F7FB] rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Shield" size={16} color="#6B7280" />
              <span className="text-xs text-[#6B7280]">Active Reveals</span>
            </div>
            <span className="text-xl md:text-2xl font-bold text-[#111827]">
              {subscription?.activeReveals || 0}
            </span>
          </div>
          <div className="p-4 bg-[#F6F7FB] rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Clock" size={16} color="#6B7280" />
              <span className="text-xs text-[#6B7280]">Pending</span>
            </div>
            <span className="text-xl md:text-2xl font-bold text-[#111827]">
              {subscription?.pendingReveals || 0}
            </span>
          </div>
        </div>

        <div className="p-4 bg-[#F6F7FB] rounded-xl">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-[#111827]">Next Billing Date</span>
            <span className="text-sm text-[#6B7280]">{subscription?.nextBilling || 'N/A'}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-[#111827]">Plan Status</span>
            <span className="text-sm text-[#10B981]">Active</span>
          </div>
        </div>

        {usagePercentage >= 80 && (
          <div className="p-4 bg-[#FEF2F2] border border-[#FEE2E2] rounded-xl">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} color="#EF4444" />
              <div className="flex-1">
                <p className="text-sm font-medium text-[#EF4444] mb-1">
                  Low on Credits
                </p>
                <p className="text-sm text-[#6B7280]">
                  You're running low on reveal credits. Consider upgrading your plan.
                </p>
              </div>
            </div>
          </div>
        )}

        <Button
          variant="default"
          fullWidth
          iconName="Zap"
          iconPosition="left"
          onClick={onUpgrade}
        >
          Upgrade Plan
        </Button>
      </div>
    </div>
  );
};

export default SubscriptionPanel;